import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { localDay, utcHour } from "@/lib/studio";

export const RANGES = [["24h", "24 hours"], ["7d", "7 days"], ["30d", "30 days"], ["12m", "12 months"], ["custom", "Custom"]];
export const GRANS = [["hour", "Hour"], ["day", "Day"], ["week", "Week"], ["month", "Month"]];

const startOf = (d, gran) => {
  const x = new Date(d);
  if (gran !== "hour") x.setHours(0, 0, 0, 0); else x.setMinutes(0, 0, 0);
  if (gran === "week") x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  if (gran === "month") x.setDate(1);
  return x;
};
const step = (d, gran) => {
  const x = new Date(d);
  if (gran === "hour") x.setHours(x.getHours() + 1);
  else if (gran === "day") x.setDate(x.getDate() + 1);
  else if (gran === "week") x.setDate(x.getDate() + 7);
  else x.setMonth(x.getMonth() + 1);
  return x;
};
const label = (d, gran) => (gran === "hour" ? d.toLocaleString(undefined, { weekday: "short", hour: "numeric" })
  : gran === "month" ? d.toLocaleDateString(undefined, { month: "short", year: "2-digit" })
  : d.toLocaleDateString(undefined, { month: "short", day: "numeric" }));

export const useStatsRange = () => {
  const [range, setRange] = useState("7d");
  const [gran, setGran] = useState("day");
  const [from, setFrom] = useState(localDay(new Date(Date.now() - 13 * 864e5)));
  const [to, setTo] = useState(localDay(new Date()));
  const { start, end } = useMemo(() => {
    const now = new Date();
    if (range === "custom") return { start: new Date(`${from}T00:00:00`), end: new Date(`${to}T23:59:59`) };
    const s = new Date(now);
    if (range === "24h") s.setHours(s.getHours() - 23, 0, 0, 0);
    else if (range === "12m") { s.setMonth(s.getMonth() - 11, 1); s.setHours(0, 0, 0, 0); }
    else { s.setDate(s.getDate() - (range === "7d" ? 6 : 29)); s.setHours(0, 0, 0, 0); }
    return { start: s, end: now };
  }, [range, from, to]);
  const pickRange = (r) => { setRange(r); setGran({ "24h": "hour", "7d": "day", "30d": "day", "12m": "month" }[r] || gran); };
  return { range, setRange: pickRange, gran, setGran, from, setFrom, to, setTo, start, end, params: { start: utcHour(start), end: utcHour(end) } };
};

const DatePick = ({ value, onChange, min, max, testid }) => {
  const [open, setOpen] = useState(false);
  const d = new Date(`${value}T00:00:00`);
  const dis = [...(min ? [{ before: new Date(`${min}T00:00:00`) }] : []), ...(max ? [{ after: new Date(`${max}T00:00:00`) }] : [])];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button variant="outline" size="sm" className="h-8 w-36 justify-start font-normal" data-testid={testid}><CalendarDays className="mr-2 h-3.5 w-3.5" />{d.toLocaleDateString()}</Button></PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={d} defaultMonth={d} disabled={dis} onSelect={(x) => { if (x) { onChange(localDay(x)); setOpen(false); } }} initialFocus /></PopoverContent>
    </Popover>
  );
};

export const RangeControls = ({ r, effective }) => (
  <div className="flex flex-wrap items-center gap-3" data-testid="stats-range-controls">
    <div className="flex overflow-hidden rounded border border-[#2D3442]">
      {RANGES.map(([k, l]) => <button key={k} onClick={() => r.setRange(k)} className={`px-3 py-1.5 text-xs ${r.range === k ? "bg-[#D97706] text-[#0B0D10]" : "text-[#A8A090] hover:bg-[#1A202C]"}`} data-testid={`stats-range-${k}`}>{l}</button>)}
    </div>
    {r.range === "custom" && <>
      <DatePick value={r.from} max={r.to} onChange={r.setFrom} testid="stats-from" />
      <span className="text-xs text-[#A8A090]">to</span>
      <DatePick value={r.to} min={r.from} onChange={r.setTo} testid="stats-to" />
    </>}
    <div className="flex overflow-hidden rounded border border-[#2D3442]">
      {GRANS.map(([k, l]) => <button key={k} disabled={k === "hour" && r.end - r.start > 8 * 864e5} onClick={() => r.setGran(k)} className={`px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${(effective || r.gran) === k ? "bg-[#1A202C] text-[#F3EBDD]" : "text-[#A8A090] hover:bg-[#1A202C]"}`} data-testid={`stats-gran-${k}`}>{l}</button>)}
    </div>
  </div>
);

export const bucketize = (hours, start, end, gran) => {
  const g = gran === "hour" && end - start > 8 * 864e5 ? "day" : gran;
  const buckets = new Map();
  for (let d = startOf(start, g); d <= end; d = step(d, g)) buckets.set(+d, { label: label(d, g), words: 0 });
  hours.forEach((h) => { const k = +startOf(new Date(`${h.hour}:00:00Z`), g); if (buckets.has(k)) buckets.get(k).words += h.words; });
  return { rows: [...buckets.values()], gran: g };
};

export const WordsChart = ({ rows, goal, unit = "words", name = "Net change", color = "#F59E0B", testid = "words-chart" }) => (
  <div className="h-64" data-testid={testid}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows}>
        <CartesianGrid stroke="#2D3442" vertical={false} />
        <XAxis dataKey="label" stroke="#A8A090" fontSize={11} tickLine={false} minTickGap={16} />
        <YAxis stroke="#A8A090" fontSize={11} tickLine={false} allowDecimals={false} />
        <Tooltip cursor={{ fill: "#F59E0B10" }} contentStyle={{ background: "#0B0D10", border: "1px solid #2D3442", color: "#F3EBDD" }} formatter={(v) => [`${v.toLocaleString()} ${unit}`, name]} />
        {goal > 0 && <ReferenceLine y={goal} stroke="#10B981" strokeDasharray="4 4" label={{ value: "goal", fill: "#10B981", fontSize: 10, position: "right" }} />}
        <ReferenceLine y={0} stroke="#2D3442" />
        <Bar dataKey="words" fill={color} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);
